import React, { useEffect, useRef, useState } from 'react';
import { isLondonScope } from '../../../lib/services/londonPlanningService';
import discoveryStyles from '../../../styles/home-discovery.module.css';

const formatCount = (value) => value == null || !Number.isFinite(Number(value))
  ? '—' : Number(value).toLocaleString('en-GB');

export default function PlanningUpdatesRegionSwitch({ activeKey, counts, onSelect, options, defaultCounts }) {
  const region = isLondonScope(activeKey) ? 'london' : 'scotland';
  const [expanded, setExpanded] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(4);
  const gridRef = useRef(null);
  const gridId = React.useId();

  useEffect(() => setExpanded(false), [region]);
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    const measure = () => {
      const columns = window.getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
      setVisibleLimit(Math.max(1, columns) * 2);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, []);

  const getCount = (option) => {
    if (option.key === 'scotland') {
      return options.filter((item) => item.countKey && !isLondonScope(item.key))
        .reduce((total, item) => total + (Number(counts[item.countKey] ?? defaultCounts[item.countKey]) || 0), 0);
    }
    return counts[option.countKey || option.key] ?? defaultCounts[option.countKey || option.key];
  };
  const sortedOptions = options.filter((option) => region === 'london'
    ? isLondonScope(option.key) : !isLondonScope(option.key))
    .sort((left, right) => {
      if (left.key === region) return -1;
      if (right.key === region) return 1;
      if (left.key === activeKey) return -1;
      if (right.key === activeKey) return 1;
      return (Number(getCount(right)) || 0) - (Number(getCount(left)) || 0)
        || left.label.localeCompare(right.label);
    });
  const hiddenCount = Math.max(0, sortedOptions.length - visibleLimit);

  return (
    <nav aria-label="Choose planning region" className="w-full">
      <div role="group" aria-label="Planning coverage" className="mb-3 grid grid-cols-2 gap-2">
        {['scotland', 'london'].map((key) => (
          <button key={key} type="button" aria-pressed={region === key}
            className="pr-segment-item border border-[var(--c-border)]"
            onClick={() => { if (region !== key) onSelect(options.find((option) => option.key === key)); }}>
            {key === 'scotland' ? 'Scotland' : 'London'}
          </button>
        ))}
      </div>
      <div ref={gridRef} id={gridId} className={discoveryStyles.councilOptions}>
        {sortedOptions.map((option, index) => (
          <button key={option.key} type="button" aria-pressed={option.key === activeKey}
            hidden={!expanded && index >= visibleLimit}
            style={!expanded && index >= visibleLimit ? { display: 'none' } : undefined}
            onClick={() => { if (option.key !== activeKey) onSelect(option); }}
            className="pr-segment-item">
            <span>{option.key === 'scotland' ? 'All Scottish councils' : option.key === 'london' ? 'All London councils' : option.label}</span>
            <span className="pr-segment-count">{formatCount(getCount(option))}</span>
          </button>
        ))}
      </div>
      <div className={discoveryStyles.councilExpandBar}>
        <span>{region === 'london' ? 'London councils' : 'Scottish councils'}</span>
        {hiddenCount > 0 && (
          <button type="button" aria-expanded={expanded} aria-controls={gridId}
            onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Show fewer councils' : `Show all councils (${hiddenCount} more)`}
            <span aria-hidden="true">{expanded ? '−' : '+'}</span>
          </button>
        )}
      </div>
    </nav>
  );
}
